import React, { Component } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import './accountProfileLayout.css';
import { Layout, Typography } from 'antd';
import { LinkContainer } from 'react-router-bootstrap'
import { UserOutlined, BankOutlined, TeamOutlined, LogoutOutlined } from '@ant-design/icons';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import firebase from 'firebase/app';
import { Redirect } from 'react-router-dom';


import { isTestModeOn, TEST_MODE_EVENT } from '../../utils/testMode';
import { ccyIsoCodeToSymbolMap } from '../../utils/CurrencyUtils';
import StrikeFooter from '../../components/StrikeFooter';
import { DelegatedViewingBanner } from '../../components/client/DelegatedViewingBanner';
import { TestModeBanner, TEST_MODE_CLIENT_COLOR } from '../../components/client/TestModeBanner';
import { getMaxAccountCcyTotal } from '../../utils/AccountCalculations';
import StrikeClientModel from '../../models/StrikeClientModel';
import StrikeAccountTransactionsModel from '../../models/StrikeAccountTransactionsModel';
import { subscribeToTeamDoc } from '../../components/client/useTeamDoc';
import placeholderImage from '../../images/placeholder_avatar.png';
import { getSidebarLogoLg, getBrandIconDark, getBrandName } from '../../utils/BrandConfig';

const { Content } = Layout;
const { Text, Title, Paragraph } = Typography;

const ccy = ccyIsoCodeToSymbolMap;

export default class TeamLayout extends Component {

  constructor(props) {
    super(props);

    const clientId = this.props.match.params.clientId;
    const teamId = this.props.match.params.teamId;

    this.baseURLs = props.baseURLs || {
      manage: '/client/manage',
      login: '/client/login'
    };

    this.clientModel = new StrikeClientModel({
      onClientFound: this.foundClient,
      onClientNotFound: this.clientNotFound,
      viewModel: this,
      clientId,
      isAdmin: false,
    });

    this.accountsModel = new StrikeAccountTransactionsModel({
      isTestMode: isTestModeOn(clientId),
      callback: this.updatedDataSource,
      viewModel: this,
      clientId,
      excludeTransactions: false,
    });

    this.state = {
      clientId: clientId,
      teamId: teamId,
      triggeredLogOut: false,
      isUseDashboard: false,
    }
  }

  componentDidMount() {
    this.clientModel.waitForClientData();
    // Chrome follows the Test Mode switch live (banner + purple navbar).
    this.onTestModeChanged = () => this.forceUpdate();
    window.addEventListener(TEST_MODE_EVENT, this.onTestModeChanged);
  }

  componentWillUnmount() {
    if (this.onTestModeChanged) window.removeEventListener(TEST_MODE_EVENT, this.onTestModeChanged);
    if (this.clientModel) this.clientModel.stop();
    // The team listener was never released here, so every team navigation left
    // one behind. The shared subscription is reference-counted, so dropping this
    // reference closes the underlying listener once nothing else wants it.
    if (this.unsubscribeTeam) this.unsubscribeTeam();
  }

  foundClient = async (client) => {

    this.setState(
      (state) => {
        return {
          clientNotFound: false,
          client,
          isUseDashboard: false,
          loadingDetails: {
            client: false,
          }
        }
      }
    );

    this.accountsModel.waitForAccountsData();
    if (!this.unsubscribeTeam) {
      this.unsubscribeTeam = subscribeToTeamDoc(
        this.state.teamId,
        (entry) => this.onUpdateTeamDetails(entry.team),
      );
    }
  }

  clientNotFound = () => {
    console.log("Client not found.");

    // This layout is also used for the claim flow and we want to avoid this issue at this check
    if (this.props.match.path !== "/client/claim/:tagId") {
      this.setState({
        clientNotFound: true
      });
    }
  }

  renderRedirectToLogin = () => {
    let toRoute = `${this.baseURLs.login}`;

    if (this.state.triggeredLogOut === false && this.props.match.path !== "/client/manage/:clientId" && this.props.match.path !== "/client/claim/:tagId") {
      const fullPath = this.props.match.url;
      const remainingRoute = fullPath.split(`${this.state.clientId}/`)[1];

      toRoute += `?redirectRoute=${remainingRoute}`;
    }

    return <Redirect to={toRoute} />
  }

  // Leave the page first, then sign out. Signing out with the dashboard still
  // mounted makes every live listener on it fail its rules check at once; in
  // production that is console noise, on localhost it is the dev error overlay
  // over a page that never moved. The redirect below unmounts the screen (and
  // this layout's own models) before the token goes.
  logOut = () => {
    this.setState({ triggeredLogOut: true }, () => {
      setTimeout(() => {
        firebase.auth().signOut().catch((err) => console.warn('[logout] signOut failed:', err && err.message));
      }, 0);
    });
  }

  updatedDataSource = async (txns) => {
    const accounts = this.accountsModel.accounts;

    this.setState(
      (state) => {
        return {
          accounts,
        }
      }
    );
  }

  onUpdateTeamDetails = async (team) => {
    this.setState(
      (state) => {
        return {
          team,
        }
      }
    );
  }

  render() {
    const clientId = this.props.match.params.clientId;
    const teamId = this.props.match.params.teamId;
    const isTestWorld = !!(this.state.clientId && isTestModeOn(this.state.clientId));
    const displayName = this.state.client ? this.state.client.displayName : "Your Profile";
    const avatarSrc = this.state.client && this.state.client.avatar ? this.state.client.avatar : placeholderImage;
    const accounts = (this.state.accounts && this.state.accounts.length > 0) ? this.state.accounts : null;
    const mainBalance = accounts ? getMaxAccountCcyTotal(accounts) : null;
    const balance = mainBalance ? (ccy[mainBalance.currency] + mainBalance.decimalBalance.toFixed(2)) : null;
    const isUseDashboard = this.state.isUseDashboard;
    const logoLink = `/client/manage/${clientId}`;

    const teamHomeLink = `/client/manage/${clientId}/teams/${teamId}`;
    const clientPageLink = `/client/manage/${clientId}`;

    if (this.state.triggeredLogOut) return this.renderRedirectToLogin();
    if (this.state.clientNotFound) return this.renderRedirectToLogin();

    return (
      <div className='team-layout vh-100'>
        <Navbar
          bg={isTestWorld ? undefined : 'primary'}
          variant="dark"
          expand="lg"
          collapseOnSelect
          style={isTestWorld ? { backgroundColor: TEST_MODE_CLIENT_COLOR } : undefined}
        >
          <Navbar.Brand href={logoLink} id="navbar-logo" className="d-flex align-items-center">
            <img
              alt={getBrandName()}
              src={getBrandIconDark()}
              className="d-lg-none mr-auto"
              style={{ height: 36, width: 36, objectFit: 'contain' }}
            />
            <img
              alt={getBrandName()}
              src={getSidebarLogoLg()}
              className="d-none d-lg-block mr-auto"
              style={{ height: 36, maxWidth: 180, objectFit: 'contain' }}
            />
          </Navbar.Brand>

          {this.state.team && (
            <div id="navbar-team-title" className="d-flex justify-content-center mx-auto">
              <Nav.Link href={teamHomeLink} className="navbar-nav mx-auto text-center">
                <Text level={2} style={{ color: 'white', marginTop: '8px' }} ellipsis={true}>{this.state.team.teamName.substring(0, 18)}</Text>
              </Nav.Link>
            </div>
          )}

          <Navbar.Toggle aria-controls="basic-navbar-nav">
            <img className="rounded-circle"
              width="40" height="40"
              src={avatarSrc}
              alt={displayName}
            />
          </Navbar.Toggle>

          <Navbar.Collapse id="basic-navbar-nav">
            <Nav>
              <LinkContainer to={clientPageLink}>
                <Nav.Link className="d-lg-none"><UserOutlined /><span>{displayName}</span></Nav.Link>
              </LinkContainer>
              <Nav.Link onClick={this.logOut} className="d-lg-none"><LogoutOutlined /> Log out</Nav.Link>

              <NavDropdown title={
                <img className="rounded-circle"
                  width="40" height="40"
                  src={avatarSrc}
                  alt={displayName}
                />
              }
                id="basic-nav-dropdown" alignRight className="d-none d-lg-block">
                <LinkContainer to={clientPageLink}>
                  <NavDropdown.Item><UserOutlined /><span> Profile</span></NavDropdown.Item>
                </LinkContainer>
                <NavDropdown.Item onClick={this.logOut}><LogoutOutlined /> Log out</NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Navbar>

        <Container fluid>
          <Content style={{ padding: '0 0 0 0', margin: '0 0 0 0' }}>
            <div className="site-layout-content">
              <TestModeBanner clientId={this.state.clientId} />
              <DelegatedViewingBanner clientId={this.state.clientId} />
              {this.props.children}
            </div>
          </Content>
          <StrikeFooter locStrings={this.props.locStrings} />
        </Container>
      </div>
    );
  }
}
